import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Divider,
  MenuItem,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  Edit,
  Delete,
  AddCircleOutline,
  Save,
  UploadFile,
  Close,
} from "@mui/icons-material";
import { addVisa } from "../../../Redux/action/AddVisaInformationAction";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FaEye } from "react-icons/fa";

const PRIMARY_COLOR = "#286a94";

const baseUrl = process.env.REACT_APP_API_PATH;

const COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Singapore",
  "UAE",
  "Germany",
  "France",
  "Japan",
  "China",
  "Netherlands",
  "Switzerland",
  "Sweden",
  "Qatar",
  "Saudi Arabia",
  "Spain",
  "Italy",
  "New Zealand",
  "Malaysia",
  "South Africa",
  "Ireland",
  "Thailand",
];

const VISA_TYPES = [
  "Visa-Free",
  "Tourist Visa",
  "Work Visa",
  "Student Visa",
  "Transit Visa",
  "On Arrival Visa",
  "eVisa",
];

const TravelHistory = ({ candidateId }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [travelList, setTravelList] = useState([]);
  const [editingTravelId, setEditingTravelId] = useState(null);
  const [openViewer, setOpenViewer] = useState(false);
  const [viewFileUrl, setViewFileUrl] = useState("");
  const [viewFileType, setViewFileType] = useState(""); // image | pdf | other
  const [previewFile, setPreviewFile] = useState(null);

  const [form, setForm] = useState({
    countryName: "",
    entryDate: "",
    exitDate: "",
    purposeOfVisit: "",
    visaTypeUsed: "",
    travelFile: null,
  });

  const handleViewFile = (fileUrl) => {
    setViewFileUrl(fileUrl);

    if (fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      setViewFileType("image");
    } else if (fileUrl.match(/\.pdf$/i)) {
      setViewFileType("pdf");
    } else {
      setViewFileType("other");
    }

    setOpenViewer(true);
  };

  const loadTravelData = async () => {
    const res = await axios.get(
      `${baseUrl}/ebench/get_visa_information/${candidateId}`
    );
    setTravelList(res.data?.data?.travelHistory || []);
  };

  useEffect(() => {
    loadTravelData();
  }, [candidateId]);

  const resetForm = () => {
    setEditingTravelId(null);
    setPreviewFile(null);
    setForm({
      countryName: "",
      entryDate: "",
      exitDate: "",
      purposeOfVisit: "",
      visaTypeUsed: "",
      travelFile: null,
    });
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setForm({ ...form, travelFile: file });
    setPreviewFile(URL.createObjectURL(file));
  };

  // Validation: Exit date must be >= Entry date
  const validateDates = () => {
    if (!form.entryDate || !form.exitDate) return false;
    return new Date(form.exitDate) >= new Date(form.entryDate);
  };

  // Add record
  const handleAdd = async () => {
    if (!validateDates()) {
      alert("Exit date must be after Entry date.");
      return;
    }

    const formData = new FormData();

    const { travelFile, ...travelHistoryWithoutFile } = form;

    const payload = {
      candidateId,
      visaInformation: [],
      travelHistory: [travelHistoryWithoutFile],
    };

    formData.append("data", JSON.stringify(payload));

    if (travelFile) {
      formData.append("travelFile", travelFile);
    }

    await dispatch(addVisa(formData));

    resetForm();
    await loadTravelData();
    toast.success("Travel History added successfully!");
  };

  // Update record
  const handleUpdate = async () => {
    if (!validateDates()) {
      alert("Exit date must be after Entry date.");
      return;
    }

    const formData = new FormData();
    const { travelFile, ...jsonData } = form;

    // JSON part
    formData.append(
      "data",
      new Blob([JSON.stringify(jsonData)], { type: "application/json" })
    );

    // File part
    if (travelFile instanceof File) {
      formData.append("travelFile", travelFile);
    }

    await axios.put(
      `${baseUrl}/ebench/update_travel/${editingTravelId}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    resetForm();
    await loadTravelData();
    toast.success("Travel History updated successfully!");
  };

  const handleDelete = async (id) => {
    await axios.delete(`${baseUrl}/ebench/delete_travel/${id}`);
    await loadTravelData();
    toast.success("Travel History deleted successfully!");
  };

  const openEdit = (record) => {
    setEditingTravelId(record.id);
    setForm({ ...record, travelFile: null });
    setTimeout(
      () => formRef.current?.scrollIntoView({ behavior: "smooth" }),
      150
    );
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ color: PRIMARY_COLOR, fontWeight: 600 }}
          >
            Travel History
          </Typography>

          <Tooltip title={editingTravelId ? "Save Changes" : "Add New Entry"}>
            <IconButton
              sx={{ color: PRIMARY_COLOR }}
              onClick={editingTravelId ? handleUpdate : handleAdd}
              disabled={!form.countryName}
            >
              {editingTravelId ? <Save /> : <AddCircleOutline />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Form */}
        <Paper
          ref={formRef}
          sx={{ p: 3, borderLeft: `4px solid ${PRIMARY_COLOR}`, mb: 3 }}
        >
          <Grid container spacing={2}>
            {/* Country Autocomplete */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={COUNTRIES}
                value={form.countryName}
                onChange={(_, v) => setForm({ ...form, countryName: v })}
                renderInput={(params) => (
                  <TextField {...params} label="Country" />
                )}
              />
            </Grid>

            {/* Visa Type */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Visa Type Used"
                name="visaTypeUsed"
                value={form.visaTypeUsed}
                onChange={handleChange}
              >
                {VISA_TYPES.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Purpose */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose of Visit"
                name="purposeOfVisit"
                value={form.purposeOfVisit}
                onChange={handleChange}
              />
            </Grid>

            {/* Dates */}
            {[
              { name: "entryDate", label: "Entry Date" },
              { name: "exitDate", label: "Exit Date" },
            ].map((field) => (
              <Grid item xs={12} md={6} key={field.name}>
                <TextField
                  fullWidth
                  type="date"
                  label={field.label}
                  name={field.name}
                  value={form[field.name]}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            ))}

            {/* File Upload */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFile />}
                sx={{ borderColor: PRIMARY_COLOR, color: PRIMARY_COLOR }}
              >
                Upload Travel Proof (Optional)
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                />
              </Button>

              {previewFile && (
                <Box sx={{ mt: 1 }}>
                  {previewFile ? (
                    <img
                      src={previewFile}
                      alt="Uploaded Preview"
                      style={{
                        maxWidth: "150px",
                        maxHeight: "150px",
                        borderRadius: 6,
                        border: "1px solid #ddd",
                      }}
                    />
                  ) : (
                    <Typography fontSize={14}>
                      📁 <strong>File Added</strong>{" "}
                      <a
                        href={previewFile}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </a>
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Cancel Edit */}
          {editingTravelId && (
            <Button
              sx={{ mt: 2 }}
              color="error"
              variant="outlined"
              onClick={resetForm}
            >
              Cancel Edit
            </Button>
          )}
        </Paper>

        <Divider sx={{ mb: 2 }} />

        {/* Table */}
        <Paper sx={{ p: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: "#f4f9fc" }}>
                <TableCell>
                  <b>Country</b>
                </TableCell>
                <TableCell>
                  <b>Entry Date</b>
                </TableCell>
                <TableCell>
                  <b>Exit Date</b>
                </TableCell>
                <TableCell>
                  <b>Purpose of Visit</b>
                </TableCell>
                <TableCell>
                  <b>Visa Used</b>
                </TableCell>
                <TableCell>
                  <b>Document</b>
                </TableCell>
                <TableCell>
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {travelList.length ? (
                travelList.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.countryName}</TableCell>
                    <TableCell>{item.entryDate}</TableCell>
                    <TableCell>{item.exitDate}</TableCell>
                    <TableCell>{item.purposeOfVisit}</TableCell>
                    <TableCell>
                      <Chip label={item.visaTypeUsed} size="small" />
                    </TableCell>
                    <TableCell>
                      {item.travelFile ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FaEye />}
                          onClick={() => handleViewFile(item.travelFile)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "20px",
                            px: 1,
                            color: PRIMARY_COLOR,
                            borderColor: PRIMARY_COLOR,
                            fontWeight: 500,
                            "&:hover": {
                              backgroundColor: `${PRIMARY_COLOR}10`,
                              borderColor: PRIMARY_COLOR,
                            },
                          }}
                        >
                          View
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          sx={{ color: PRIMARY_COLOR }}
                          onClick={() => openEdit(item)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={7}>
                    No travel records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
      <Dialog
        open={openViewer}
        onClose={() => setOpenViewer(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: PRIMARY_COLOR,
          }}
        >
          View File
          <IconButton onClick={() => setOpenViewer(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {viewFileType === "image" && (
            <img
              src={viewFileUrl}
              alt="Preview"
              style={{ width: "100%", borderRadius: 8 }}
            />
          )}

          {viewFileType === "pdf" && (
            <iframe
              src={viewFileUrl}
              title="PDF Viewer"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          )}

          {viewFileType === "other" && (
            <Typography>
              Preview not supported.
              <br />
              <a href={viewFileUrl} target="_blank" rel="noopener noreferrer">
                Download file
              </a>
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TravelHistory;
